import { useMemo, useState } from "react";
import { Button } from "V2.0/common/button";
import { Dialog, DialogTitle, DialogBody } from "V2.0/common/dialog";
import Form from "V2.0/components/Form";
import { Cog6ToothIcon } from "@heroicons/react/24/outline";
import { getTransferSettingsByAssistantType } from "./getTransferSettingByAssistant";
import { ASSISTANT } from "../Assistant";
import { ShowOriginalCallerID } from "../VoiceSettings/ShowOriginalCallerId";
import PostTransferTranscriptionInfo from "./components/PostTransferTranscriptionInfo";
import TransferTypeInfo from "./components/TransferTypeInfo";

const buttonInfo = {
  buttons: "2",
  primaryLabel: "Save",
  secondaryLabel: "Cancel",
};

export function TransferSettings(props) {
  const {
    settings,
    updateSettings,
    assistantType,
    voiceSettings,
    workspaceId,
    assistantId,
    syncAssistant,
  } = props;

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    postTransferTranscriptionEnabled:
      settings?.postTransferTranscriptionEnabled ?? false,
    transferType: settings?.transferType ?? "cold",
  });

  const defaultSettings = useMemo(
    () => ({
      whatsappTransferTrigger: settings?.whatsappTransferTrigger || "",
      transferMessage: settings?.transferMessage || "",
      transferOnMediaMessage: settings?.transferOnMediaMessage || false,
      officeHours: settings?.officeHours || false,
      humanDelay: settings?.humanDelay || 0,
      transferTrigger: settings?.transferTrigger || "",
      postTransferTranscriptionEnabled:
        settings?.postTransferTranscriptionEnabled ?? false,
      postTransferTranscriptionDelay:
        settings?.postTransferTranscriptionDelay ?? 0,
      maxPostTransferTranscriptionDuration:
        settings?.maxPostTransferTranscriptionDuration ?? 0,
      transferType: settings?.transferType ?? "cold",
      holdMusicUrl: settings?.holdMusicUrl ?? "",
      warmTransferFallbackMessage:
        settings?.warmTransferFallbackMessage ??
        "I'm sorry, the agent wasn't available at the moment. How else can I help you?",
      warmTransferRingTimeout: settings?.warmTransferRingTimeout ?? 30,
      agentBriefingLanguage: settings?.agentBriefingLanguage ?? "en-US",
    }),
    [settings],
  );

  const { template, schema, inputField } = useMemo(() => {
    const transferSettings = getTransferSettingsByAssistantType(
      assistantType,
      defaultSettings,
    );

    return transferSettings;
  }, [assistantType, defaultSettings]);

  const updatedTemplate = useMemo(() => {
    if (assistantType === ASSISTANT.VOICE) {
      const updatedTemplate = template.reduce((acc, field) => {
        if (field.name === "postTransferTranscriptionEnabled") {
          const updatedField = {
            ...field,
            info: <PostTransferTranscriptionInfo />,
          };
          acc.push(updatedField);
          return acc;
        }

        if (field.name === "transferType") {
          const updatedField = {
            ...field,
            info: <TransferTypeInfo />,
          };
          acc.push(updatedField);
          return acc;
        }

        if (
          (field.name === "holdMusicUrl" && formData?.transferType != "warm") ||
          (field.name === "postTransferTranscriptionDelay" &&
            !formData?.postTransferTranscriptionEnabled) ||
          (field.name === "maxPostTransferTranscriptionDuration" &&
            !formData?.postTransferTranscriptionEnabled) ||
          (field.name === "warmTransferFallbackMessage" &&
            formData?.transferType !== "warm") ||
          (field.name === "warmTransferRingTimeout" &&
            formData?.transferType != "warm") ||
          (field.name === "agentBriefingLanguage" &&
            formData?.transferType != "warm")
        ) {
          return acc;
        }

        acc.push(field);
        return acc;
      }, []);

      return updatedTemplate;
    }

    return template;
  }, [
    template,
    assistantType,
    formData?.transferType,
    formData?.postTransferTranscriptionEnabled,
  ]);

  function onSave(data) {
    updateSettings(data)
      .then(() => {
        setLoading(false);
        setOpen(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <>
      <Button
        color="white"
        onClick={() => setOpen(true)}
      >
        <Cog6ToothIcon /> Settings
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
      >
        <DialogTitle>Configure Transfer Settings</DialogTitle>
        <DialogBody>
          {assistantType === ASSISTANT.VOICE && (
            <ShowOriginalCallerID
              showOriginalCallerId={voiceSettings?.showOriginalCallerId}
              workspaceId={workspaceId}
              assistantId={assistantId}
              syncAssistant={syncAssistant}
            />
          )}
          <Form
            inputField={inputField}
            loading={loading}
            setLoading={setLoading}
            buttonInfo={buttonInfo}
            handleSecondary={() => setOpen(false)}
            onSubmit={onSave}
            template={updatedTemplate}
            schema={schema}
            getValue={(data) => setFormData(data)}
          />
        </DialogBody>
      </Dialog>
    </>
  );
}
